import { useCallback, useEffect, useRef, useState } from 'react';

import { LayoutMetrics, calculateLayoutMetrics } from '../../layout-geometry';
import { LayoutRendererProps } from './layout-renderer.component';

interface ViewProps {
    layoutBox: React.RefObject<HTMLDivElement | null>;
    containerStyle: React.CSSProperties;
    scale: number;
    layoutKey: string;
}

const useLayoutRenderer = (props: LayoutRendererProps): ViewProps => {
    const layoutBox = useRef<HTMLDivElement>(null);
    const [layoutMetrics, changeLayoutMetrics] = useState<LayoutMetrics>({
        containerStyle: {
            position: 'relative',
            width: 0,
            height: 0
        },
        scale: 1
    });

    const updateLayoutMetrics = useCallback(() => {
        const currentLayoutBox = layoutBox.current;

        if (!currentLayoutBox) return;

        changeLayoutMetrics(calculateLayoutMetrics(props.containerInfo, currentLayoutBox.clientWidth, currentLayoutBox.clientHeight));
    }, [props.containerInfo]);

    useEffect(() => {
        updateLayoutMetrics();

        const currentLayoutBox = layoutBox.current;

        if (!currentLayoutBox) return;

        if (typeof ResizeObserver !== 'undefined') {
            const resizeObserver = new ResizeObserver(updateLayoutMetrics);
            resizeObserver.observe(currentLayoutBox);

            return () => resizeObserver.disconnect();
        }

        window.addEventListener('resize', updateLayoutMetrics);

        return () => window.removeEventListener('resize', updateLayoutMetrics);
    }, [updateLayoutMetrics]);

    return {
        layoutBox,
        containerStyle: layoutMetrics.containerStyle,
        scale: layoutMetrics.scale,
        layoutKey: [
            props.containerInfo.minX,
            props.containerInfo.minY,
            props.containerInfo.maxX,
            props.containerInfo.maxY,
            layoutMetrics.containerStyle.width,
            layoutMetrics.containerStyle.height
        ].join(':')
    };
};

export default useLayoutRenderer;
